import { useCallback, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { ApolloQueryResult } from '@apollo/client/core/types'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { GetExpensablesQuery } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SubmitOcrAnimation from 'modules/expenses/components/common/SubmitOcrAnimation'
import { buildErrorMessages } from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import { useCreateFromDocument, useUpdateFromDocument } from 'modules/expenses/hooks/useExpensable'
import { SignableOrganizationPerCountry } from 'modules/expenses/hooks/useTaxAgencyHomologations'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import {
  trackCreatedExpensable,
  trackEditExpensable,
} from 'modules/expenses/lib/expensables/expensableTracking'
import { Expensable } from 'modules/expenses/types/Expensable'

import { useFilesPreviewer } from '../../../hooks/useFilesPreviewer'

interface UseUploadFileDialogProps {
  expensable?: Expensable
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  refetchExpensable?: () => Promise<ApolloQueryResult<GetExpensablesQuery>>
}

const useUploadFileDialog = ({
  expensable,
  taxAgencyOrganization,
  refetchExpensable,
}: UseUploadFileDialogProps) => {
  const [dialogState, setDialogState] = useState<{
    show: boolean
    files: File[]
  }>({ show: false, files: [] })

  const { getValues, setValue, trigger } = useFormContext()
  const { updateFromDocument, loading: updatingFromDocument } = useUpdateFromDocument()
  const { createFromDocument, loading: creatingFromDocument } = useCreateFromDocument()
  const { company } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const previewer = useFilesPreviewer()
  const { section } = useOutletContext<RouteContextParams>()

  const handleJustUpload = useCallback(
    (files: File[]) => {
      const oldFiles = getValues('files') || []
      const newFiles = oldFiles.concat(files)

      previewer.attachFiles(files)
      setValue('files', newFiles, { shouldDirty: true })
      trigger('files')
    },
    [getValues, setValue, trigger, previewer]
  )

  const handleUpdateFromDocument = useCallback(
    async (files: File[]) => {
      if (!expensable) return

      const result = await updateFromDocument({
        id: expensable.id,
        files: files,
        filesIdToRemove: [],
        signedForOrganization: taxAgencyOrganization,
        documentOrigin: 'file',
      })

      const handled = handleResult(result, (errors) => {
        const errorMessages = buildErrorMessages(errors)
        app.setFlash({
          type: 'wrong',
          message: errorMessages || i18n.t('errors.generic_error'),
        })
      })

      if (!handled.success) {
        trackEditExpensable(expensable, {
          ocr: true,
          action: 'edit',
          success: false,
          context: section,
        })
        return handled
      }

      const updatedExpensable = handled.data?.expensable
      if (!updatedExpensable) return handled

      trackEditExpensable(updatedExpensable, {
        ocr: true,
        previous_status: expensable.status,
        action: 'edit',
        success: true,
        context: section,
      })

      refetchExpensable?.()
      return handled
    },
    [expensable, taxAgencyOrganization, updateFromDocument, refetchExpensable, section]
  )

  const handleCreateFromDocument = useCallback(
    async (files: File[]) => {
      const groupId = getValues('group')?.id

      const result = await createFromDocument({
        files: files,
        signedForOrganization: taxAgencyOrganization,
        documentOrigin: 'file',
        companyId: company.id,
        employeeId: getValues('employee')?.id,
        groupId: groupId,
      })

      const handled = handleResult(result, (errors) => {
        const errorMessages = buildErrorMessages(errors)
        app.setFlash({
          type: 'wrong',
          message: errorMessages || i18n.t('errors.generic_error'),
        })
      })

      if (!handled.success) {
        return handled
      }

      const createdExpensable = handled.data?.expensable
      if (!createdExpensable) return handled

      const expensableId = createdExpensable.id
      trackCreatedExpensable({
        id: expensableId,
        type: 'expense',
        status: createdExpensable.status,
        expense: null,
      })

      if (groupId) {
        navigate(
          navigation.myExpenses.groups.id.expenseId.fullPath({
            groupId: groupId,
            expense_id: expensableId,
          })
        )
      } else {
        navigate(
          navigation.myExpenses.list.expenses.expenseId.fullPath({
            expense_id: expensableId,
          })
        )
      }

      return handled
    },
    [
      taxAgencyOrganization,
      createFromDocument,
      company,
      getValues,
      navigate,
      navigation.myExpenses.list.expenses.expenseId,
      navigation.myExpenses.groups.id.expenseId,
    ]
  )

  const handleScanAndUpload = useCallback(
    async (files: File[]) => {
      if (expensable) {
        return await handleUpdateFromDocument(files)
      } else {
        return await handleCreateFromDocument(files)
      }
    },
    [expensable, handleUpdateFromDocument, handleCreateFromDocument]
  )

  const openDialog = useCallback((files: File[]) => {
    setDialogState({ show: true, files })
  }, [])

  const closeDialog = useCallback(() => {
    setDialogState({ show: false, files: [] })
  }, [])

  const loadingScanFiles = updatingFromDocument || creatingFromDocument

  const Dialog = dialogState.show ? (
    <>
      {!loadingScanFiles && (
        <Modal
          title={i18n.t('expenses_v2.forms.fields.dropzone.dialog.title')}
          description={i18n.t('expenses_v2.forms.fields.dropzone.dialog.description')}
          illustration="document-folder-disorder-list"
          onClose={closeDialog}
          footer={
            <Box gap="s12" justifyContent="flexEnd" flexDirection="row">
              <Button
                type="secondary"
                onClick={() => {
                  handleJustUpload(dialogState.files)
                  closeDialog()
                }}
              >
                {i18n.t('expenses_v2.forms.fields.dropzone.dialog.just_upload')}
              </Button>
              <Button
                type="primary"
                onClick={async () => {
                  await handleScanAndUpload(dialogState.files)
                  closeDialog()
                }}
              >
                {i18n.t('expenses_v2.forms.fields.dropzone.dialog.scan_upload')}
              </Button>
            </Box>
          }
        />
      )}
      {/* OCR Loading modal */}
      {loadingScanFiles && (
        <Modal illustration="document-folder-disorder-list" title="" locked disableFocusTrap>
          <Box
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="full"
            gap="s6"
            tabIndex={0}
          >
            <SubmitOcrAnimation operation={expensable ? 'update' : 'create'} />
          </Box>
        </Modal>
      )}
    </>
  ) : null

  return {
    openDialog,
    handleJustUpload,
    Dialog,
    loading: loadingScanFiles,
  }
}

export default useUploadFileDialog
